// src/components/staff/StaffManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { staffService } from '../../services/staffService';
import { organizationService } from '../../services/organizationService';
import { premisesService } from '../../services/premisesService';
import StaffTable from './StaffTable';
import StaffForm from './StaffForm';
import StaffTransfers from './StaffTransfers';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchableSelect from '../common/SearchableSelect';
import Pagination from '../common/Pagination';
import Toast from '../common/Toast';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatAttendanceTime } from '../../utils/dateUtils';

const StaffManagement = () => {
  usePageTitle('Staff Management');
  const ACTIVITY_PAGE_SIZE = 10;
  const { user } = useAuth();
  const location = useLocation();
  const [activeAlertFilter, setActiveAlertFilter] = useState(location.state?.filter || null);
  const [staff, setStaff] = useState([]);
  const [premises, setPremises] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showTransfers, setShowTransfers] = useState(false);
  const [viewingStaff, setViewingStaff] = useState(null);
  const [staffActivityLoading, setStaffActivityLoading] = useState(false);
  const [staffActivityData, setStaffActivityData] = useState({
    staff: null,
    attendance: [],
    login_logs: [],
    summary: {}
  });
  const [staffActivityAttendancePage, setStaffActivityAttendancePage] = useState(1);
  const [staffActivityLoginPage, setStaffActivityLoginPage] = useState(1);

  // Bulk upload states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkDesignations, setBulkDesignations] = useState([]);
  const [bulkStaffImages, setBulkStaffImages] = useState([]);
  const [bulkUploadMethod, setBulkUploadMethod] = useState('separate');
  const [selectedOrgForBulk, setSelectedOrgForBulk] = useState('');
  const [selectedPremisesForBulk, setSelectedPremisesForBulk] = useState('');
  const [countries, setCountries] = useState([]);

  const [bulkUploadPremises, setBulkUploadPremises] = useState([]);
  const [showPremisesTable, setShowPremisesTable] = useState(false); // Toggle table visibility
  const [premisesTableLoading, setPremisesTableLoading] = useState(false); // Loading state for premises table
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    organization_id: '',
    premises_id: '',
    status: '',
    page: 1,
    limit: 20
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    present_today: 0,
    app_admin: 0
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1
  });

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load data on component mount
  useEffect(() => {
    if (user) {
      initializeComponent();
    }
  }, [user]);

  // Load staff when filters change (except during initial load)
  useEffect(() => {
    if (user && filters.page > 0) { // Only load after filters are properly initialized
      loadStaff();
      loadStaffStats();
    }
  }, [filters, user]);

  const initializeComponent = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // First, load the necessary data
      await Promise.all([
        loadOrganizations(),
        loadPremises(),
        loadCountries()
      ]);

      // Then apply role-based setup and load staff
      await applyRoleBasedFilters();

    } catch (error) {
      console.error('Error initializing component:', error);
      setError('Failed to initialize staff management');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilters = async () => {
    const newFilters = { ...filters };

    // Set role-based default filters but don't restrict the view
    if (user?.user_type === 'app_admin' && user?.premises_id) {
      // App admin: Load their premises' designations but show all staff in their premises
      await loadDesignationsForPremises(user.premises_id);
    } else if (user?.user_type === 'client_admin' && user?.org_id) {
      // Client admin: Show all staff in their organization
      // Load premises for their org to enable filtering
      await loadPremisesForOrganization(user.org_id);
    }
    // Core admin: Show all staff across all organizations

    // Apply alert pre-filter from dashboard navigation
    if (location.state?.filter === 'inactive') {
      newFilters.status = 'inactive';
    }

    // Update filters to trigger initial data load
    setFilters(newFilters);
  };

  const loadStaff = async () => {
    try {
      setLoading(true);

      // Build API parameters based on user role and filters
      const apiParams = {};

      // Add search if present
      if (filters.search) {
        apiParams.search = filters.search;
      }

      // Add pagination
      apiParams.page = filters.page;
      apiParams.limit = filters.limit;

      // Add status filter
      if (filters.status) {
        apiParams.status = filters.status;
      }

      // Add designation filter
      if (filters.designation) {
        apiParams.designation = filters.designation;
      }

      // Role-based filtering
      if (user?.user_type === 'core_admin') {
        // Core admin: apply organization and premises filters if selected
        if (filters.organization_id) {
          apiParams.organization_id = filters.organization_id;
        }
        if (filters.premises_id) {
          apiParams.premises_id = filters.premises_id;
        }
      } else if (user?.user_type === 'client_admin') {
        // Org admin: always filter by their org, optionally by premises
        apiParams.organization_id = user.org_id;
        if (filters.premises_id) {
          apiParams.premises_id = filters.premises_id;
        }
      } else if (user?.user_type === 'app_admin') {
        // App admin: always filter by their premises
        apiParams.premises_id = user.premises_id;
      }

      const response = await staffService.getStaff(apiParams);
      setStaff(response.staff || []);
      setPagination(response.pagination || {});
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading staff:', err);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };



  const loadPremises = async () => {
    try {
      let response;
      if (user?.user_type === 'core_admin') {
        // Core admin gets all premises across all organizations - use the new endpoint
        response = await organizationService.getAllPremises();
      } else if (user?.user_type === 'client_admin') {
        // Org admin gets premises for their organization
        response = await organizationService.getOrganizationPremises(user.org_id);
      } else if (user?.user_type === 'app_admin') {
        // App admin gets only their premises
        response = await premisesService.getPremises();
      }

      let filteredPremises = response?.premises || [];

      // Further filter based on user role
      if (user?.user_type === 'app_admin' && user?.premises_id) {
        filteredPremises = filteredPremises.filter(p => p.id === user.premises_id);
      }

      setPremises(filteredPremises);
    } catch (err) {
      console.error('Error loading premises:', err);
      setPremises([]);
    }
  };

  const loadPremisesForForm = async (orgId = null) => {
    try {
      let response;

      if (user?.user_type === 'core_admin') {
        if (orgId) {
          // Load premises for selected organization
          // Backend now returns org_category_id from the organization table
          response = await organizationService.getOrganizationPremises(orgId);
        } else {
          setPremises([]);
          return;
        }
      } else if (user?.user_type === 'client_admin') {
        // Backend now returns org_category_id from the organization table
        response = await organizationService.getOrganizationPremises(user.org_id);
      } else if (user?.user_type === 'app_admin') {
        // Backend now returns org_category_id from the organization table
        response = await premisesService.getPremises();
        if (user?.premises_id) {
          response.premises = response.premises?.filter(p => p.id === user.premises_id) || [];
        }
      }

      setPremises(response?.premises || []);
    } catch (err) {
      console.error('Error loading premises for form:', err);
      setPremises([]);
      showToast('Failed to load premises', 'error');
    }
  };

  const handleOrganizationChangeInForm = async (orgId) => {
    // Clear current premises and designations when organization changes
    setDesignations([]);

    // Load premises for the selected organization
    await loadPremisesForForm(orgId);
  };

  const loadOrganizations = async () => {
    if (user?.user_type !== 'core_admin') return;

    try {
      const response = await organizationService.getOrganizations({ limit: 1000 });
      setOrganizations(response.organizations || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setOrganizations([]);
    }
  };

  const loadStaffStats = async () => {
    try {
      const apiParams = {};

      // Apply same filtering logic as loadStaff for stats
      if (user?.user_type === 'core_admin') {
        if (filters.organization_id) {
          apiParams.organization_id = filters.organization_id;
        }
        if (filters.premises_id) {
          apiParams.premises_id = filters.premises_id;
        }
      } else if (user?.user_type === 'client_admin') {
        apiParams.organization_id = user.org_id;
        if (filters.premises_id) {
          apiParams.premises_id = filters.premises_id;
        }
      } else if (user?.user_type === 'app_admin') {
        apiParams.premises_id = user.premises_id;
      }

      const response = await staffService.getStaffStats(apiParams);
      setStats(response || { total: 0, active: 0, inactive: 0, present_today: 0, app_admin: 0 });
    } catch (err) {
      console.error('Error loading staff stats:', err);
      setStats({ total: 0, active: 0, inactive: 0, present_today: 0, app_admin: 0 });
    }
  };

  const loadCountries = async () => {
    try {
      const data = await organizationService.getCountriesWithDialCodes();
      setCountries(data.countries || []);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
      setCountries([]);
    }
  };

  const loadDesignationsForPremises = async (premisesId) => {
    if (!premisesId) {
      setDesignations([]);
      return;
    }
    try {
      // Find the premises object in the state array using the premisesId
      const selectedPremises = premises.find(p => p.id == premisesId);
      if (selectedPremises?.org_category_id) {
        // Use organizationService.getDesignations with the premisesId
        const data = await organizationService.getDesignations(selectedPremises.org_category_id, premisesId);
        setDesignations(data.designations || []);
      } else {
        // If premises is not found in the state or lacks org_category_id, try fetching it specifically
        // This might be needed if premises list is large or filtered, and the specific premise isn't loaded
        console.warn(`Premises ${premisesId} not found in current list or missing org_category_id. Fetching premises details...`);

        // Fetch the specific premise's details including org_category_id
        const premiseDetails = await premisesService.getPremisesById(premisesId);

        if (premiseDetails?.org_category_id) {
          const data = await organizationService.getDesignations(premiseDetails.org_category_id, premisesId);
          setDesignations(data.designations || []);
        } else {
          console.error(`Premises ${premisesId} does not have org_category_id`);
          setDesignations([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch designations:', error);
      setDesignations([]);
      // Optionally show an error toast here
      // showToast('Failed to load designations for the selected premises.', 'error');
    }
  };

  // Handle organization change for core admin
  const handleOrganizationChange = async (orgId) => {
    setFilters(prev => ({
      ...prev,
      organization_id: orgId,
      premises_id: '', // Reset premises when org changes
      page: 1
    }));

    // Reset designations
    setDesignations([]);

    if (orgId) {
      // Load premises for selected organization
      await loadPremisesForOrganization(orgId);
    } else {
      // Load all premises for core admin
      await loadPremises();
    }
  };

  const loadPremisesForOrganization = async (orgId) => {
    try {
      const response = await organizationService.getOrganizationPremises(orgId);
      setPremises(response.premises || []);
    } catch (err) {
      console.error('Error loading premises for organization:', err);
      setPremises([]);
    }
  };

  const handlePremisesChange = async (premisesId) => {
    setFilters(prev => ({
      ...prev,
      premises_id: premisesId,
      page: 1
    }));

    // Load designations for the selected premises
    await loadDesignationsForPremises(premisesId);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'organization_id') {
      handleOrganizationChange(value);
    } else if (key === 'premises_id') {
      handlePremisesChange(value);
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value,
        page: 1
      }));
    }
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleAddStaff = async () => {
    if (!canCreateStaff()) {
      showToast('You do not have permission to create staff members', 'error');
      return;
    }
    setEditingStaff(null);

    // Load appropriate premises for the form
    if (user?.user_type === 'core_admin') {
      // For core admin, clear premises until organization is selected
      setPremises([]);
      setDesignations([]);
    } else if (user?.user_type === 'app_admin') {
      // app_admin: premises and designations are already loaded at startup — just refresh premises
      await loadPremisesForForm();
      // designations remain valid (app_admin's premises never changes)
    } else {
      await loadPremisesForForm();
      setDesignations([]);
    }

    setShowForm(true);
  };

  const handleEditStaff = async (staff) => {
    if (!canEditStaff(staff)) {
      showToast('You do not have permission to edit this staff member', 'error');
      return;
    }
    setEditingStaff(staff);

    // Load premises based on the staff's organization IF needed for context, but designations depend on premises_id
    // For client_admin/core_admin, we might have loaded premises for the org already, or need to ensure premises list is available
    // The critical part is loading premises for the *specific* staff member's premises_id to get its designations
    // Check if premises for the staff's org are already loaded, if not, load them
    // This assumes loadPremisesForForm loads premises based on the staff's org_id if no specific orgId is passed for non-core_admin
    // If user is core_admin, we might need to load premises for the staff's specific org
    if (user?.user_type === 'core_admin' && staff.org_id) {
      await loadPremisesForForm(staff.org_id);
    } else {
      // For client_admin and app_admin, load their accessible premises (this should already be done on initial load, but ensure it is)
      await loadPremisesForForm(); // This should load premises for client_admin's org or app_admin's premise
    }

    // Load designations FOR THE SPECIFIC PREMISES THE STAFF MEMBER IS ASSIGNED TO
    if (staff.premises_id) {
      await loadDesignationsForPremises(staff.premises_id); // Pass the staff's premises_id
    } else {
      // If staff doesn't have a premises_id, clear designations or handle accordingly
      setDesignations([]); // Or show a message
    }
    setShowForm(true);
  };

  const handleDeleteStaff = async (staffMember) => {
    if (!canDeleteStaff(staffMember)) {
      showToast('You do not have permission to delete staff members', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
      return;
    }

    try {
      await staffService.deleteStaff(staffMember.id);
      await loadStaff();
      await loadStaffStats();
      showToast('Staff member deleted successfully!', 'success');
    } catch (err) {
      showToast('Error deleting staff member: ' + err.message, 'error');
    }
  };

  const handleViewStaff = async (staffMember) => {
    if (!staffMember?.id) {
      showToast('Invalid staff selected', 'error');
      return;
    }

    setViewingStaff(staffMember);
    setStaffActivityLoading(true);
    setStaffActivityData({
      staff: null,
      attendance: [],
      login_logs: [],
      summary: {}
    });
    setStaffActivityAttendancePage(1);
    setStaffActivityLoginPage(1);

    try {
      const response = await staffService.getStaffActivity(staffMember.id, {
        attendance_limit: 120,
        login_limit: 80
      });
      setStaffActivityData(response || {
        staff: null,
        attendance: [],
        login_logs: [],
        summary: {}
      });
    } catch (err) {
      showToast('Failed to load user activities: ' + err.message, 'error');
    } finally {
      setStaffActivityLoading(false);
    }
  };


  const handleStaffSubmit = async (staffData, photoFile) => { // staffData now includes user_type
    try {
      if (editingStaff) {
        // For updates, pass the staffData object which includes user_type
        // Check if photoFile exists to decide which service function to use
        if (photoFile) {
          await staffService.updateStaffWithPhoto(editingStaff.id, staffData, photoFile);
        } else {
          // Ensure staffData includes the ID for the update
          await staffService.updateStaff(editingStaff.id, staffData); // Pass staffData including user_type
        }
        showToast('Staff member updated successfully!', 'success');
      } else {
        // For creation, pass the staffData object which includes user_type
        if (photoFile) {
          await staffService.createStaffWithPhoto(staffData, photoFile); // Pass staffData including user_type
        } else {
          await staffService.createStaff(staffData); // Pass staffData including user_type
        }
        showToast('Staff member created successfully!', 'success');
      }
      setShowForm(false);
      setEditingStaff(null);
      await loadStaff(); // Reload staff list
      await loadStaffStats(); // Reload stats if necessary
    } catch (err) {
      showToast('Error saving staff member: ' + err.message, 'error');
    }
  };

  const handleExport = async (format) => {
    try {
      const exportFilters = {};

      // Apply same filtering logic for export
      if (user?.user_type === 'core_admin') {
        if (filters.organization_id) exportFilters.organization_id = filters.organization_id;
        if (filters.premises_id) exportFilters.premises_id = filters.premises_id;
      } else if (user?.user_type === 'client_admin') {
        exportFilters.organization_id = user.org_id;
        if (filters.premises_id) exportFilters.premises_id = filters.premises_id;
      } else if (user?.user_type === 'app_admin') {
        exportFilters.premises_id = user.premises_id;
      }

      if (filters.search) exportFilters.search = filters.search;
      if (filters.status) exportFilters.status = filters.status;

      await staffService.exportStaff(format, exportFilters);
      showToast(`Staff data exported as ${format.toUpperCase()} successfully!`, 'success');
    } catch (err) {
      showToast('Error exporting data: ' + err.message, 'error');
    }
  };

  // Bulk upload handlers




  const openBulkUploadModal = async () => {
    if (!canCreateStaff()) {
      showToast('You do not have permission to upload staff', 'error');
      return;
    }

    // Reset state for the modal
    setBulkUploadFile(null);
    setBulkStaffImages([]);
    setBulkUploadMethod('separate');
    setBulkUploadResult(null);
    setSelectedOrgForBulk(['client_admin', 'app_admin'].includes(user.user_type) ? user.org_id : ''); // Pre-select for org-scoped admins
    setBulkDesignations([]); // Reset bulk designations reference
    setBulkUploadPremises([]); // Reset bulk premises reference (NEW)
    setShowPremisesTable(false); // Hide premises table initially (NEW)

    try {
      // Determine the organization ID to get data for
      let orgIdToUse = user.org_id; // Default to user's org (works for client_admin and app_admin)
      if (user.user_type === 'core_admin' && selectedOrgForBulk) {
        orgIdToUse = selectedOrgForBulk; // Use selected org for core_admin
      }

      // Fetch organization details to get org_category_id for designations
      const orgDetails = await organizationService.getOrganizationById(orgIdToUse);
      if (orgDetails && orgDetails.org_category_id) {
        // Fetch designations for the organization's category
        const designationsData = await organizationService.getDesignations(orgDetails.org_category_id, null); // null for premises_id
        setBulkDesignations(designationsData.designations || []);

        // Fetch premises for the organization for the reference table
        const premisesData = await organizationService.getOrganizationPremises(orgIdToUse);
        setBulkUploadPremises(premisesData.premises || []); // Set premises for reference (NEW)

      } else {
        console.warn(`Could not find org_category_id for org ID ${orgIdToUse}. Cannot load designations or premises.`);
        setBulkDesignations([]); // Clear designations if category ID is missing
        setBulkUploadPremises([]); // Clear premises if category ID is missing (NEW)
      }

    } catch (err) {
      console.error('Error fetching data (designations/premises) for bulk upload reference:', err);
      showToast('Failed to load data for reference.', 'error');
      setBulkDesignations([]); // Ensure state is cleared on error
      setBulkUploadPremises([]); // Ensure premises state is cleared on error (NEW)
    }

    setShowBulkUploadModal(true);
  };



  const handleBulkOrgChange = async (orgId) => {
    setSelectedOrgForBulk(orgId);
    setSelectedPremisesForBulk('');
    setBulkDesignations([]);

    if (orgId) {
      await loadPremisesForOrganization(orgId);
    }
  };

  const fetchDesignationsForBulkUpload = async (premisesId) => {
    if (!premisesId) return;

    try {
      const selectedPremises = premises.find(p => p.id == premisesId);
      if (selectedPremises?.org_category_id) {
        const data = await organizationService.getDesignations(
          selectedPremises.org_category_id,
          premisesId
        );
        setBulkDesignations(data.designations || []);
      }
    } catch (error) {
      console.error('Failed to fetch designations:', error);
      setBulkDesignations([]);
    }
  };


  const handleBulkUpload = async (e) => {
    e.preventDefault();

    // Validation: Check if file is selected
    if (!bulkUploadFile) { // Removed check for selectedPremisesForBulk
      showToast('Please select a file.', 'error'); // Updated message
      return;
    }

    // Optional: Add more validation if needed (e.g., check CSV content for premises_id column)

    setBulkUploadLoading(true);
    try {
      const orgId = selectedOrgForBulk || user.org_id; // Use selected org (core_admin) or user's org (client_admin/app_admin)
      // Call the service function WITHOUT the premisesId parameter
      const result = await staffService.bulkUploadStaff(
        orgId,
        bulkUploadFile, // Pass the file
        bulkUploadMethod === 'separate' ? bulkStaffImages : [], // Pass images if separate method
        bulkUploadMethod // Pass the method
        // REMOVED: selectedPremisesForBulk argument
      );

      setBulkUploadResult(result);
      showToast(`Bulk upload completed! ${result.success_count} staff created, ${result.error_count} errors`, 'success');
      // Refresh staff list
      await loadStaff();
      await loadStaffStats();

    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleOrganizationChangeInBulkModal = async (orgId) => {
    setSelectedOrgForBulk(orgId);
    if (orgId) {
      try {
        setPremisesTableLoading(true); // Set loading state
        const premisesData = await organizationService.getOrganizationPremises(orgId);
        setBulkUploadPremises(premisesData.premises || []);
      } catch (err) {
        console.error('Error fetching premises for bulk upload reference:', err);
        showToast('Failed to load premises for reference.', 'error');
        setBulkUploadPremises([]); // Clear on error
      } finally {
        setPremisesTableLoading(false); // Clear loading state
      }
    } else {
      setBulkUploadPremises([]); // Clear if org is deselected
    }
  };




  // NEW: Function to download ZIP template instructions for staff
  const downloadStaffZipTemplate = (designations) => { // Pass designations for sample
    // Reuse the CSV content generation logic
    const headers = [
      'name', // required
      'staff_id', // required
      'designation', // will be linked to premises_id or org_category_id
      'phone_number', // required - will also be the username
      'email', // required
      'gender', // optional
      'dob', // optional - YYYY-MM-DD
      'date_of_appointment', // optional - YYYY-MM-DD
      'premises_id', // NEW: required in CSV
      'image_filename' // optional if using separate method
    ];

    // Sample data - include premises_id (user needs to provide this)
    const sampleRows = [
      ['John Doe', 'STF001', designations[0]?.designation_name || 'Manager', '8012345678', 'john@company.com', 'Male', '1990-05-15', '2024-01-15', '123', 'john_photo.jpg'], // Include premises_id '123'
      ['Jane Smith', 'STF002', designations[1]?.designation_name || 'Assistant', '8087654321', 'jane@company.com', 'Female', '1992-08-20', '2024-02-01', '456', 'jane_photo.jpg'] // Include premises_id '456'
    ];

    let csvContent = headers.join(',') + '\n'; // Add newline after headers

    // Add comments/instructions (optional)
    csvContent += '# CSV Template Instructions\n';
    csvContent += '# IMPORTANT: The "premises_id" column is now required.\n';
    csvContent += '# The "username" will be automatically set to the "phone_number".\n';
    csvContent += '# Ensure the premises_id exists in your organization.\n';
    csvContent += '# If the designation does not exist for the specified premises_id, it will be created.\n';
    csvContent += '# Converts everything to YYYY-MM-DD before insert.\n';
    csvContent += '# Wrap fields containing commas in double quotes.\n';
    csvContent += '# Remove these comment lines before uploading.\n';

    sampleRows.forEach(row => {
      // Escape values and join with commas
      const escapedRow = row.map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
          return '"' + value.replace(/"/g, '""') + '"';
        }
        return value;
      });
      csvContent += escapedRow.join(',') + '\n';
    });

    // Create ZIP template instruction content
    const instructionsContent = `ZIP Package Template Instructions for Staff Upload
=============================================

1.  **Create a ZIP file** with the following structure:
    staff-upload.zip
    ├── staff.csv (required - see template below)
    ├── john_photo.jpg (optional - image files)
    ├── jane_photo.png (optional - image files)
    
2.  **CSV File Requirements:**
    *   The CSV file must be named exactly "staff.csv".
    *   It must be placed in the **root** of the ZIP file.
    *   The structure of the CSV is shown below.

3.  **Image File Requirements:**
    *   Place your staff image files in the root of the ZIP or in an "images/" subfolder.
    *   The filenames in the CSV (in the 'image_filename' column) must **exactly** match the actual filenames of the image files in the ZIP.
    *   Supported image formats: JPG, JPEG, PNG, GIF.

4.  **CSV Template:**
    ===============
${csvContent}

5.  **Important Notes:**
    - Ensure all fields in the CSV containing commas (like addresses or names with commas) are wrapped in double quotes.
    - Example of a field with a comma: "Doe, John"
    - The 'image_filename' column should only contain the filename (e.g., 'john_photo.jpg'), not a path.
    - The ZIP file should be uploaded using the "ZIP Package" option in the bulk upload modal.
`;

    // Create and download the instruction text file
    const instructionsBlob = new Blob([instructionsContent], { type: 'text/plain' });
    const instructionsUrl = window.URL.createObjectURL(instructionsBlob);
    const instructionsLink = document.createElement('a');
    instructionsLink.href = instructionsUrl;
    instructionsLink.download = `zip_template_instructions_staff_${user?.org_id}.txt`; // Use user's org_id in filename
    document.body.appendChild(instructionsLink);
    instructionsLink.click();
    document.body.removeChild(instructionsLink);
    window.URL.revokeObjectURL(instructionsUrl);

    // Show a toast confirming the file was downloaded
    showToast('ZIP template instructions downloaded. Please follow the instructions to create your ZIP package.', 'info');
  };


  const downloadStaffCSVTemplate = async (premisesId = null) => { // Accept premisesId optionally
    const headers = [
      'name', // required
      'staff_id', // required
      'designation', // will be linked to premises_id or org_category_id
      'phone_number', // required - will also be the username
      'email', // required
      'gender', // optional
      'dob', // optional - YYYY-MM-DD
      'date_of_appointment', // optional - YYYY-MM-DD
      'premises_id', // NEW: required in CSV
      'image_filename' // optional if using separate method
    ];

    let sampleDesignation1 = 'Manager'; // Default placeholder
    let sampleDesignation2 = 'Assistant'; // Default placeholder

    // Attempt to get designations for the specific premises ID to use in sample rows
    if (premisesId) {
      try {
        // Use the existing service function to fetch designations for the premises
        // Note: This requires the service function to be available or replicate its logic
        // For now, assuming a service call is possible or a local state holds them for the selected premises in the modal
        // If bulkDesignations state holds designations for the currently selected premises in the modal:
        // const premisesDesignations = bulkDesignations; // If bulkDesignations are for the selected premises
        // Or fetch them specifically:
        const premisesDesignations = await organizationService.getDesignations(null, premisesId); // Use premisesId to get designations
        if (premisesDesignations && premisesDesignations.designations && premisesDesignations.designations.length > 0) {
          sampleDesignation1 = premisesDesignations.designations[0]?.designation_name || sampleDesignation1;
          sampleDesignation2 = premisesDesignations.designations[1]?.designation_name || sampleDesignation2;
        }
        // If the call fails or returns no designations, defaults remain
      } catch (err) {
        console.error("Error fetching designations for template sample:", err);
        // Keep defaults if fetching fails
      }
    }

    // Sample data - include premises_id, username derived from phone, and use fetched/placeholder designations
    const sampleRows = [
      ['John Doe', 'STF001', sampleDesignation1, '8012345678', 'john@company.com', 'Male', '1990-05-15', '2024-01-15', '123', 'john_photo.jpg'], // Include premises_id '123'
      ['Jane Smith', 'STF002', sampleDesignation2, '8087654321', 'jane@company.com', 'Female', '1992-08-20', '2024-02-01', '456', 'jane_photo.jpg'] // Include premises_id '456'
    ];

    let csvContent = headers.join(',') + '\n'; // Add newline after headers

    // Add comments/instructions (optional)
    csvContent += '# CSV Template Instructions\n';
    csvContent += '# IMPORTANT: The "premises_id" column is now required.\n';
    csvContent += '# The "username" will be automatically set to the "phone_number".\n';
    csvContent += '# Ensure the premises_id exists in your organization.\n';
    csvContent += '# If the designation does not exist for the specified premises_id, it will be created.\n';
    csvContent += '# Converts everything to YYYY-MM-DD before insert.\n';
    csvContent += '# Wrap fields containing commas in double quotes.\n';
    csvContent += '# Remove these comment lines before uploading.\n';

    sampleRows.forEach(row => {
      // Escape values and join with commas
      // Using the same escapeCSVValue helper logic inline
      const escapedRow = row.map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
          return '"' + value.replace(/"/g, '""') + '"';
        }
        return value;
      });
      csvContent += escapedRow.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Use org name or user ID in filename
    const orgNameOrId = user.user_type === 'client_admin' ? user.org_id : selectedOrgForBulk || 'all';
    a.download = `staff_template_${orgNameOrId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  const closeBulkUploadModal = useCallback(() => {
    setShowBulkUploadModal(false);
    setBulkUploadFile(null);
    setBulkUploadResult(null);
    setBulkDesignations([]);
    setBulkStaffImages([]);
    setSelectedOrgForBulk('');
    setSelectedPremisesForBulk('');
  }, []);

  // Permission helpers
  const canCreateStaff = () => {
    return ['core_admin', 'client_admin', 'app_admin'].includes(user?.user_type);
  };

  const canEditStaff = (staff) => {
    if (user?.user_type === 'core_admin') return true;
    if (user?.user_type === 'client_admin') {
      return staff?.org_id === user?.org_id;
    }
    if (user?.user_type === 'app_admin') {
      return staff?.premises_id === user?.premises_id;
    }
    return false;
  };

  const canDeleteStaff = (staff) => {
    // Only core admin can delete staff
    return user?.user_type === 'core_admin';
  };

  const clearFilters = () => {
    // Reset to default state - no filters applied, just role-based access control
    const baseFilters = {
      search: '',
      organization_id: '',
      premises_id: '',
      status: '',
      designation: '',
      page: 1,
      limit: 20
    };

    setFilters(baseFilters);

    // Reset designations based on role
    if (user?.user_type === 'app_admin' && user?.premises_id) {
      // App admin keeps their premises' designations loaded
      loadDesignationsForPremises(user.premises_id);
    } else {
      // Others clear designations until premises is selected
      setDesignations([]);
    }

    // Reset premises for client admin if organization filter was cleared
    if (user?.user_type === 'client_admin' && user?.org_id) {
      loadPremisesForOrganization(user.org_id);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const attendanceRows = staffActivityData?.attendance || [];
  const loginRows = staffActivityData?.login_logs || [];
  const attendanceTotalPages = Math.max(1, Math.ceil(attendanceRows.length / ACTIVITY_PAGE_SIZE));
  const loginTotalPages = Math.max(1, Math.ceil(loginRows.length / ACTIVITY_PAGE_SIZE));
  const pagedAttendanceRows = attendanceRows.slice(
    (staffActivityAttendancePage - 1) * ACTIVITY_PAGE_SIZE,
    staffActivityAttendancePage * ACTIVITY_PAGE_SIZE
  );
  const pagedLoginRows = loginRows.slice(
    (staffActivityLoginPage - 1) * ACTIVITY_PAGE_SIZE,
    staffActivityLoginPage * ACTIVITY_PAGE_SIZE
  );

  if (loading && staff.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="staff-management">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>
              <i className="fas fa-users"></i>
              Staff Management
            </h1>
            <p>
              {user?.user_type === 'core_admin'
                ? 'Manage all staff across all organizations'
                : user?.user_type === 'client_admin'
                  ? 'Manage all staff in your organization'
                  : 'Manage all staff in your premises'
              }
            </p>
          </div>
          <div className="header-right">
            {canCreateStaff() && (
              <button
                className="btn btn-secondary"
                onClick={openBulkUploadModal}
              >
                <i className="fas fa-upload"></i>
                Bulk Upload
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => setShowTransfers(true)}
            >
              <i className="fas fa-exchange-alt"></i>
              View Transfers
            </button>
            {canCreateStaff() && (
              <button
                className="btn btn-primary"
                onClick={handleAddStaff}
              >
                <i className="fas fa-user-plus"></i>
                Add Staff Member
              </button>
            )}
          </div>
        </div>

        {/* Staff Statistics */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon primary">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.total}</h3>
              <p>Total Staff</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <i className="fas fa-user-check"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.active}</h3>
              <p>Active Staff</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">
              <i className="fas fa-user-times"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.inactive}</h3>
              <p>Inactive Staff</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon info">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.app_admin}</h3>
              <p>App Admins</p>
            </div>
          </div>
        </div>

        {/* Alert filter banner */}
        {activeAlertFilter === 'inactive' && (
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', background:'rgba(108,117,125,0.08)', border:'1px solid #6c757d', borderRadius:'8px', padding:'0.6rem 1rem', marginBottom:'0.75rem' }}>
            <i className="fas fa-user-slash" style={{ color:'#6c757d' }}></i>
            <span style={{ flex:1, fontSize:'0.9rem', fontWeight:600 }}>Showing inactive staff members</span>
            <button onClick={() => { setActiveAlertFilter(null); setFilters(prev => ({ ...prev, status: '', page: 1 })); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.85rem', color:'var(--text-light)', display:'flex', alignItems:'center', gap:'0.3rem' }}>
              <i className="fas fa-times"></i> Clear filter
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="filter-section">
          <div className="filters-header">
            <h3>
              <i className="fas fa-filter"></i>
              Filters
            </h3>
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
              <i className="fas fa-eraser"></i>
              Clear Filters
            </button>
          </div>

          <div className="filters-grid">
            <div className="filter-group">
              <label>Search</label>
              <div className="search-box">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name, ID, or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Organization filter for core admin */}
            {user?.user_type === 'core_admin' && (
              <div className="filter-group">
                <label>Organization</label>
                <SearchableSelect
                  value={filters.organization_id}
                  onChange={(value) => handleFilterChange('organization_id', value)}
                  options={organizations}
                  placeholder="All Organizations"
                  displayKey="name"
                  valueKey="id"
                  searchPlaceholder="Search organizations..."
                />
              </div>
            )}

            <div className="filter-group">
              <label>Premises</label>
              <select
                className="form-input"
                value={filters.premises_id}
                onChange={(e) => handleFilterChange('premises_id', e.target.value)}
              >
                <option value="">
                  {user?.user_type === 'app_admin' ? 'Your Premises' : 'All Premises'}
                </option>
                {premises.map(premise => (
                  <option key={premise.id} value={premise.id}>
                    {premise.premises_name}
                    {user?.user_type === 'core_admin' && premise.organization_name &&
                      ` (${premise.organization_name})`
                    }
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                className="form-input"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Disabled</option>
                <option value="on_leave">On Leave</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Staff Table */}
        <StaffTable
          staff={staff}
          loading={loading}
          pagination={pagination}
          onView={handleViewStaff}
          onEdit={handleEditStaff}
          onDelete={handleDeleteStaff}
          onPageChange={handlePageChange}
          onExport={handleExport}
          canEdit={canEditStaff}
          canDelete={canDeleteStaff}
          showOrgColumn={user?.user_type === 'core_admin'}
          userRole={user?.user_type}
        />

        {/* Staff Form Modal */}
        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingStaff(null);
          }}
          title={editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
          size="large"
        >
          <StaffForm
            staff={editingStaff}
            premises={premises}
            organizations={user?.user_type === 'core_admin' ? organizations : []}
            onSubmit={handleStaffSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingStaff(null);
            }}
            userRole={user?.user_type}
            countries={countries}
            designations={designations}
            onPremisesChange={loadDesignationsForPremises}
            onOrganizationChange={handleOrganizationChangeInForm}
            userOrgCountryCode={user?.country_code || null}
          />
        </Modal>

        {/* Staff Activity View Modal */}
        <Modal
          isOpen={!!viewingStaff}
          onClose={() => {
            setViewingStaff(null);
            setStaffActivityData({
              staff: null,
              attendance: [],
              login_logs: [],
              summary: {}
            });
            setStaffActivityAttendancePage(1);
            setStaffActivityLoginPage(1);
          }}
          title={`User Activities: ${staffActivityData?.staff?.name || viewingStaff?.name || ''}`}
          size="xlarge"
        >
          {staffActivityLoading ? (
            <div className="activity-loading">
              <LoadingSpinner />
              <div className="activity-loading-text">Loading activities...</div>
            </div>
          ) : (
            <div className="staff-activity-modal">
              <div className="activity-summary-grid">
                <div className="activity-card">
                  <strong className="activity-card-label">User</strong>
                  <div className="activity-card-value">{staffActivityData?.staff?.name || '-'}</div>
                  <div className="activity-card-sub">{staffActivityData?.staff?.email || '-'}</div>
                </div>
                <div className="activity-card">
                  <strong className="activity-card-label">Premises</strong>
                  <div className="activity-card-value">{staffActivityData?.staff?.premises_name || '-'}</div>
                  <div className="activity-card-sub">Staff ID: {staffActivityData?.staff?.staff_id || '-'}</div>
                </div>
                <div className="activity-card">
                  <strong className="activity-card-label">Attendance Records</strong>
                  <div className="activity-card-value">{staffActivityData?.summary?.total_attendance_records ?? 0}</div>
                  <div className="activity-card-sub">Logins: {staffActivityData?.summary?.total_login_events ?? 0}</div>
                </div>
              </div>

              <div className="activity-section">
                <div className="activity-section-header">
                  <h4>Attendance History</h4>
                  <div className="activity-section-meta">
                    Page {staffActivityAttendancePage} of {attendanceTotalPages} • Total {attendanceRows.length}
                  </div>
                </div>
                <div className="activity-table-wrap">
                  <table className="table activity-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Premises</th>
                      <th>Sign In</th>
                      <th>Sign Out</th>
                      <th>Status</th>
                      <th>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAttendanceRows.map((row) => (
                      <tr key={`att-${row.id}`}>
                        <td>{formatDate(row.date)}</td>
                        <td>{row.premises_name || '-'}</td>
                        <td>{formatAttendanceTime(row.sign_in_time)}</td>
                        <td>{formatAttendanceTime(row.sign_out_time)}</td>
                        <td>{row.status || '-'}</td>
                        <td>{row.working_hours || '-'}</td>
                      </tr>
                    ))}
                    {pagedAttendanceRows.length === 0 && (
                      <tr><td colSpan="6" className="activity-empty-cell">No attendance records</td></tr>
                    )}
                  </tbody>
                  </table>
                </div>
                <div className="activity-pagination">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={staffActivityAttendancePage <= 1}
                    onClick={() => setStaffActivityAttendancePage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="activity-page-text">{staffActivityAttendancePage}/{attendanceTotalPages}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={staffActivityAttendancePage >= attendanceTotalPages}
                    onClick={() => setStaffActivityAttendancePage((p) => Math.min(attendanceTotalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="activity-section">
                <div className="activity-section-header">
                  <h4>Login Activities</h4>
                  <div className="activity-section-meta">
                    Page {staffActivityLoginPage} of {loginTotalPages} • Total {loginRows.length}
                  </div>
                </div>
                <div className="activity-table-wrap">
                  <table className="table activity-table">
                  <thead>
                    <tr>
                      <th>Login Time</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLoginRows.map((row) => (
                      <tr key={`log-${row.id}`}>
                        <td>{formatDateTime(row.created_at)}</td>
                        <td>{row.ip || '-'}</td>
                      </tr>
                    ))}
                    {pagedLoginRows.length === 0 && (
                      <tr><td colSpan="2" className="activity-empty-cell">No login records</td></tr>
                    )}
                  </tbody>
                  </table>
                </div>
                <div className="activity-pagination">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={staffActivityLoginPage <= 1}
                    onClick={() => setStaffActivityLoginPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="activity-page-text">{staffActivityLoginPage}/{loginTotalPages}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={staffActivityLoginPage >= loginTotalPages}
                    onClick={() => setStaffActivityLoginPage((p) => Math.min(loginTotalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Bulk Upload Modal */}
        <Modal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          title="Bulk Upload Staff"
          size="large"
        >
          <form onSubmit={handleBulkUpload}>
            {/* Organization Selection (for Core Admin) */}
            {user.user_type === 'core_admin' && (
              <div className="form-group">
                <label>Select Organization *</label>
                <SearchableSelect
                  value={selectedOrgForBulk}
                  onChange={handleOrganizationChangeInBulkModal} // Use the new handler
                  options={organizations}
                  placeholder="Select organization for premises reference"
                  displayKey="name"
                  valueKey="id"
                  searchPlaceholder="Search organizations..."
                  required
                />
              </div>
            )}

            {/* Upload Method Selection */}
            <div className="upload-method-selection">
              <h4>Choose Upload Method:</h4>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="separate"
                    checked={bulkUploadMethod === 'separate'}
                    onChange={() => setBulkUploadMethod('separate')}
                  />
                  <span>Separate Files (CSV + Images)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="uploadMethod"
                    value="zip"
                    checked={bulkUploadMethod === 'zip'}
                    onChange={() => setBulkUploadMethod('zip')}
                  />
                  <span>ZIP Package (CSV + Images)</span>
                </label>
              </div>
            </div>

            {/* Format Information - Conditional based on method */}
            <div className="bulk-upload-info">
              <div className="info-box">
                <i className="fas fa-info-circle"></i>
                <div>
                  {bulkUploadMethod === 'zip' ? (
                    <>
                      <h4>ZIP Package Requirements:</h4>
                      <ul>
                        <li><strong>staff.csv</strong> - CSV file with staff data (required, must be in root)</li>
                        <li><strong>Image files</strong> - Staff profile images (optional, can be in root or subfolder)</li>
                        <li><strong>Structure example:</strong>
                          <pre className="zip-structure">
                            {`staff-upload.zip
                                ├── staff.csv
                                ├── john_photo.jpg
                                ├── jane_photo.png
                            `}
                          </pre>
                        </li>
                      </ul>
                      <p><small>Converts everything to <code>YYYY-MM-DD</code> before insert.</small></p>
                      {/*
                      <div className="csv-format-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <strong>Important:</strong> The <code>premises_id</code> column is required in staff.csv.
                        The <code>username</code> will be set to <code>phone_number</code>.
                        Wrap fields containing commas in double quotes.
                      </div>*/}
                    </>
                  ) : (
                    <>
                      <h4>CSV Format Requirements:</h4>
                      <div className="csv-format-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <strong>Important:</strong> The <code>premises_id</code> column is now required. The <code>username</code> will be set to <code>phone_number</code>.
                        Wrap fields containing commas in double quotes. Converts everything to <code>YYYY-MM-DD</code> before insert.
                      </div>
                      <ul>
                        <li><strong>name</strong> (required)</li>
                        <li><strong>staff_id</strong> (required)</li>
                        <li><strong>designation</strong> (will be linked to premises_id, see reference table)</li>
                        <li><strong>phone_number</strong> (required - becomes username)</li>
                        <li><strong>email</strong> (required)</li>
                        <li><strong>gender</strong> (optional)</li>
                        <li><strong>dob</strong> (optional - YYYY-MM-DD)</li>
                        <li><strong>date_of_appointment</strong> (optional - YYYY-MM-DD)</li>
                        <li><strong>premises_id</strong> (required - see premises reference table)</li>
                        <li><strong>image_filename</strong> (optional for separate method)</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* File Uploads - Conditional based on method */}
            {bulkUploadMethod === 'zip' ? (
              <div className="form-group">
                <label>Upload ZIP Package *</label>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setBulkUploadFile(e.target.files[0])}
                  className="form-input"
                  required
                />
                <small>ZIP file containing staff.csv and staff images</small>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Upload CSV File *</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setBulkUploadFile(e.target.files[0])}
                    className="form-input"
                    required
                  />
                  <small>CSV file containing staff data</small>
                </div>
                <div className="form-group">
                  <label>Upload Staff Images </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      // Basic validation: Check file types
                      const validFiles = files.filter(file => file.type.startsWith('image/'));
                      if (validFiles.length !== files.length) {
                        showToast('Some files are not valid images.', 'error');
                      }
                      setBulkStaffImages(validFiles);
                    }}
                    className="form-input"
                  />
                  <small>Upload staff profile images. Filename should match the <code>image_filename</code> column in CSV.</small>
                </div>
              </>
            )}

            {/* Template Download - Conditional based on method */}
            <div className="form-group">
              <label>Download Template</label>
              {bulkUploadMethod === 'zip' ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => downloadStaffZipTemplate(bulkDesignations)} // Use ZIP template function
                >
                  <i className="fas fa-file-archive"></i> Download ZIP Template Instructions
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => downloadStaffCSVTemplate(null)} // Pass null for premisesId, use defaults in template
                >
                  <i className="fas fa-download"></i> Download CSV Template
                </button>
              )}
              <small>Download template file(s) with example data.</small>
            </div>

            {/* Designations Reference Table */}
            <div className="designations-reference">
              <h4>Designations Reference (for CSV)</h4>
              <p>
                <small className="text-muted">
                  Use these designations in the <code>designation</code> column of your CSV file.
                  If a designation doesn't exist for a specific premises, it will be created.
                </small>
              </p>
              <div className="reference-table">
                {bulkDesignations.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Designation Name</th>
                        <th>Type</th> {/* Common or Premises-specific */}
                      </tr>
                    </thead>
                    <tbody>
                      {bulkDesignations.map((designation) => (
                        <tr key={designation.id}>
                          <td>{designation.id}</td>
                          <td>{designation.designation_name}</td>
                          <td>
                            {designation.premises_id === 0 ? 'Common (All Premises)' : `Specific to Premises ${designation.premises_id}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <i className="fas fa-user-tag"></i>
                    <h4>No designations found</h4>
                    <p>No designations available for the selected organization's category.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Premises Reference Table */}
            <div className="premises-reference">
              <h4>Premises Reference (for CSV)</h4>
              <p>
                <small className="text-muted">
                  Use the IDs from this table in the <code>premises_id</code> column of your CSV file.
                </small>
              </p>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setShowPremisesTable(!showPremisesTable)}
              >
                {showPremisesTable ? 'Hide Premises' : 'Show Premises'}
              </button>

              {showPremisesTable && (
                <div className="reference-table">
                  {premisesTableLoading ? (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading premises...</p>
                    </div>
                  ) : bulkUploadPremises.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkUploadPremises.map((premise) => (
                          <tr key={premise.id}>
                            <td>{premise.id}</td>
                            <td>{premise.premises_name}</td>
                            <td>{premise.address}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-state">
                      <i className="fas fa-building"></i>
                      <h4>No premises found</h4>
                      <p>No premises available for the selected organization.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bulk Upload Results */}
            {bulkUploadResult && (
              <div className="bulk-results">
                <h4>Upload Results:</h4>
                <p>✅ Success: {bulkUploadResult.success_count} staff created</p>
                <p>❌ Errors: {bulkUploadResult.error_count} staff failed</p>
                {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                  <div className="error-details">
                    <h5>Error Details:</h5>
                    <ul>
                      {bulkUploadResult.errors.slice(0, 5).map((error, index) => (
                        <li key={index}>Row {error.row}: {error.message}</li>
                      ))}
                    </ul>
                    {bulkUploadResult.errors.length > 5 && (
                      <p>... and {bulkUploadResult.errors.length - 5} more errors</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Modal Actions */}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBulkUploadModal(false)} disabled={bulkUploadLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={bulkUploadLoading || !bulkUploadFile}>
                {bulkUploadLoading ? (
                  <>
                    <div className="spinner"></div> Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-upload"></i> Upload Staff
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>

        {/* Staff Transfers Modal */}
        <Modal
          isOpen={showTransfers}
          onClose={() => setShowTransfers(false)}
          title="Staff Transfer History"
          size="xlarge"
          maxWidth="1400px"
        >
          <StaffTransfers 
            onClose={() => setShowTransfers(false)}
          />
        </Modal>

        {/* Toast */}
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      </div>

      {/* Modern Gradient Styles */}
      <style jsx>{`
        .staff-management {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 2rem 0;
        }

        .staff-activity-modal {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .activity-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 0.75rem;
        }

        .activity-card {
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 12px;
        }

        .activity-card-label {
          display: block;
          font-size: 0.78rem;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .activity-card-value {
          font-size: 1rem;
          font-weight: 700;
          color: #111827;
        }

        .activity-card-sub {
          margin-top: 2px;
          color: #6b7280;
          font-size: 0.82rem;
        }

        .activity-section {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #fff;
          padding: 10px;
        }

        .activity-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 8px;
        }

        .activity-section-header h4 {
          margin: 0;
          font-size: 0.98rem;
          color: #111827;
        }

        .activity-section-meta {
          font-size: 0.8rem;
          color: #6b7280;
        }

        .activity-table-wrap {
          max-height: 260px;
          overflow: auto;
          border: 1px solid #eef2f7;
          border-radius: 8px;
        }

        .activity-table {
          width: 100%;
          min-width: 720px;
        }

        .activity-empty-cell {
          text-align: center;
          color: #6b7280;
        }

        .activity-pagination {
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
        }

        .activity-page-text {
          font-size: 0.85rem;
          color: #374151;
          min-width: 44px;
          text-align: center;
        }

        .activity-loading {
          padding: 18px 0;
          text-align: center;
        }

        .activity-loading-text {
          margin-top: 10px;
          color: #6b7280;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .page-header {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-left: 5px solid #3b82f6;
        }

        .header-left h1 {
          color: #2d3748;
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .header-left h1 i {
          font-size: 2rem;
          color: #3b82f6;
        }

        .header-left p {
          color: #718096;
          font-size: 1rem;
          margin: 0;
        }

        .header-right {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          gap: 1.2rem;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .stat-icon.primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-left-color: #3b82f6;
        }

        .stat-card:nth-child(1) {
          border-left-color: #3b82f6;
        }

        .stat-icon.success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .stat-card:nth-child(2) {
          border-left-color: #10b981;
        }

        .stat-icon.warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .stat-card:nth-child(3) {
          border-left-color: #f59e0b;
        }

        .stat-icon.info {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }

        .stat-card:nth-child(4) {
          border-left-color: #8b5cf6;
        }

        .stat-info {
          flex: 1;
        }

        .stat-info h3 {
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
          line-height: 1;
          margin: 0 0 0.3rem 0;
        }

        .stat-info p {
          font-size: 0.9rem;
          color: #718096;
          font-weight: 500;
          margin: 0;
        }

        .filter-section {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .filters-header h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }

        .filters-header h3 i {
          color: #3b82f6;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.5rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .filter-group label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .search-box {
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.8rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #3b82f6;
          font-size: 0.95rem;
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          border-left: 4px solid #dc2626;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .header-left h1 {
            justify-content: center;
          }

          .header-right {
            flex-direction: column;
            width: 100%;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        .bulk-upload-info {
          margin: 1.5rem 0;
        }

        .info-box {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: var(--radius-md);
          padding: var(--spacing-lg);
          margin-bottom: var(--spacing-md);
        }

        .info-box h4 {
          color: var(--text-dark);
          margin: 0 0 var(--spacing-sm) 0;
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .csv-format-warning {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          padding: var(--spacing-sm);
          border-radius: var(--radius-sm);
          margin: var(--spacing-sm) 0;
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          color: #856404;
        }

        .upload-method-selection {
          margin-bottom: var(--spacing-lg);
        }

        .radio-group {
          display: flex;
          gap: var(--spacing-lg);
          margin-top: var(--spacing-sm);
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          cursor: pointer;
        }

        .designations-reference {
          margin-top: var(--spacing-lg);
        }

        .reference-table {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e9ecef;
          border-radius: var(--radius-sm);
        }

        .table {
          width: 100%;
          border-collapse: collapse;
        }

        .table th,
        .table td {
          padding: var(--spacing-sm);
          text-align: left;
          border-bottom: 1px solid #e9ecef;
        }

        .table th {
          background: #f8f9fa;
          font-weight: 600;
          color: var(--text-dark);
        }

        .bulk-results {
          background: #e8f5e8;
          border: 1px solid #c3e6c3;
          border-radius: var(--radius-md);
          padding: var(--spacing-lg);
          margin: var(--spacing-lg) 0;
        }

        .error-details {
          margin-top: var(--spacing-md);
        }

        .error-details ul {
          margin: var(--spacing-sm) 0 0 0;
          padding-left: var(--spacing-lg);
        }

        .error-details li {
          margin-bottom: var(--spacing-xs);
          color: #721c24;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-md);
            text-align: center;
          }

          .header-right {
            flex-direction: column;
            width: 100%;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          .radio-group {
            flex-direction: column;
          }
        }

        /* === Bulk Upload Modal Specific Styles === */
  .upload-method-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .upload-method-selection h4 {
    margin: 0 0 16px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #ffffff;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .radio-label:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .radio-label input[type="radio"]:checked + span {
    color: #111827;
    font-weight: 600;
  }

  .radio-label span {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }

  .bulk-upload-info {
    margin-bottom: 20px;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .info-box i {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-box p {
    margin: 0;
    color: #1e40af;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }

  .info-box h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  .info-box ul {
    margin: 4px 0;
    padding-left: 16px;
  }

  .info-box li {
    margin: 4px 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.4;
  }

  .csv-format-warning {
    background: #fef3c7;
    border: 1px solid #fbbf24;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 10px 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .csv-format-warning i {
    color: #d97706;
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .csv-format-warning strong {
    color: #92400e;
  }

  .premises-reference {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .premises-reference h4 {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .reference-table {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    margin-top: 10px;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
  }

  .table th,
  .table td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px;
  }

  .table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .table td {
    color: #1f2937;
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  .table tr:hover {
    background: #f9fafb;
  }

  .bulk-results {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .bulk-results h4 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #065f46;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bulk-results h4 i {
    font-size: 18px;
  }

  .error-details {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .error-details h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0;
    padding-left: 16px;
  }

  .error-details li {
    margin: 3px 0;
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
  }

  .loading-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .loading-state .spinner {
    width: 20px;
    height: 20px;
    margin: 0 auto 12px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .empty-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .empty-state i {
    color: #d1d5db;
    font-size: 2rem;
    margin-bottom: 10px;
  }

  .empty-state h4 {
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 6px 0;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* === Bulk Upload Modal Specific Styles === */
  .upload-method-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .upload-method-selection h4 {
    margin: 0 0 16px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #ffffff;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .radio-label:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .radio-label input[type="radio"]:checked + span {
    color: #111827;
    font-weight: 600;
  }

  .radio-label span {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }

  .bulk-upload-info {
    margin-bottom: 20px;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .info-box i {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-box p {
    margin: 0;
    color: #1e40af;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }

  .info-box h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  .info-box ul {
    margin: 4px 0;
    padding-left: 16px;
  }

  .info-box li {
    margin: 4px 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.4;
  }

  .csv-format-warning {
    background: #fef3c7;
    border: 1px solid #fbbf24;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 10px 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .csv-format-warning i {
    color: #d97706;
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .csv-format-warning strong {
    color: #92400e;
  }

  .premises-reference,
  .designations-reference { /* Apply common styles to both reference tables */
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .premises-reference h4,
  .designations-reference h4 {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .reference-table {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    margin-top: 10px;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
  }

  .table th,
  .table td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px;
  }

  .table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .table td {
    color: #1f2937;
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  .table tr:hover {
    background: #f9fafb;
  }

  .bulk-results {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .bulk-results h4 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #065f46;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bulk-results h4 i {
    font-size: 18px;
  }

  .error-details {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .error-details h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0;
    padding-left: 16px;
  }

  .error-details li {
    margin: 3px 0;
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
  }

  .loading-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .loading-state .spinner {
    width: 20px;
    height: 20px;
    margin: 0 auto 12px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .empty-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .empty-state i {
    color: #d1d5db;
    font-size: 2rem;
    margin-bottom: 10px;
  }

  .empty-state h4 {
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 6px 0;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

    .upload-method-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .upload-method-selection h4 {
    margin: 0 0 16px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #ffffff;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .radio-label:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .radio-label input[type="radio"]:checked + span {
    color: #111827;
    font-weight: 600;
  }

  .radio-label span {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }

  .bulk-upload-info {
    margin-bottom: 20px;
    width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    /* Use flexbox but ensure content wraps */
    display: flex;
    flex-direction: column; /* Stack icon and content vertically */
    align-items: flex-start; /* Align content to the start */
    gap: 12px; /* Space between icon and main content block */
    box-sizing: border-box;
    width: 100%;
    overflow-wrap: break-word;
  }

  .info-box i {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0; /* Don't shrink the icon */
  }

  .info-box > div {
    /* The container for the main text content block */
    width: 100%; /* Take full width */
    overflow-wrap: break-word; /* Allow text inside to break */
  }

  .info-box p {
    margin: 0;
    color: #1e40af;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    /* Ensure paragraphs inside break correctly */
    overflow-wrap: break-word;
  }

  .info-box h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  .info-box ul {
    margin: 4px 0;
    padding-left: 16px;
    overflow-wrap: break-word; /* Ensure list items break */
  }

  .info-box li {
    margin: 4px 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.4;
    overflow-wrap: break-word; /* Ensure list item text breaks */
  }

  .csv-format-warning {
    background: #fef3c7;
    border: 1px solid #fbbf24;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 10px 0;
    /* Ensure the warning block itself wraps */
    width: 100%;
    box-sizing: border-box;
    overflow-wrap: break-word;
    /* Use flex to align icon and text, allowing text to wrap */
    display: flex;
    align-items: flex-start; /* Align icon to top */
    gap: 8px; /* Space between icon and text */
  }

  .csv-format-warning i {
    color: #d97706;
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0; /* Don't shrink the icon */
  }

  .csv-format-warning strong {
    color: #92400e;
    /* Ensure strong tags inside warning wrap */
    display: inline-block; /* Allows better control over wrapping */
    overflow-wrap: break-word;
  }

  .csv-format-warning code {
    /* Ensure code tags inside warning wrap */
    overflow-wrap: break-word;
    word-break: break-word; /* Break long code snippets */
  }

  .csv-examples {
    background: #f0f9ff;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto; /* Scroll horizontally for wide code */
  }

  .csv-examples h5 {
    margin: 0 0 6px 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e40af;
  }

  .csv-examples ul {
    margin: 0;
  }

  .csv-examples li {
    font-size: 12px;
    color: #1e40af;
  }

  .csv-examples code {
    background: #e0f2fe;
    color: #0369a1;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
    white-space: pre-wrap;
    word-break: break-all;
    display: block;
    max-width: 100%;
  }

  .premises-reference,
  .designations-reference {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    width: 100%;
    box-sizing: border-box;
  }

  .premises-reference h4,
  .designations-reference h4 {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .reference-table {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    width: 100%;
    box-sizing: border-box;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
  }

  .table th,
  .table td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px;
    word-break: break-word;
  }

  .table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .table td {
    color: #1f2937;
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  .table tr:hover {
    background: #f9fafb;
  }

  .bulk-results {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .bulk-results h4 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #065f46;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bulk-results h4 i {
    font-size: 18px;
  }

  .error-details {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .error-details h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0;
    padding-left: 16px;
  }

  .error-details li {
    margin: 3px 0;
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
  }

  .loading-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .loading-state .spinner {
    width: 20px;
    height: 20px;
    margin: 0 auto 12px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .empty-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .empty-state i {
    color: #d1d5db;
    font-size: 2rem;
    margin-bottom: 10px;
  }

  .empty-state h4 {
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 6px 0;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .form-error {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 4px;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* --- NEW RESPONSIVE STYLES --- */
  @media (max-width: 768px) {
    .info-box {
      padding: 14px;
      gap: 10px;
    }

    .info-box h4 {
      font-size: 13px;
    }

    .info-box p,
    .info-box li {
      font-size: 12px;
      line-height: 1.4;
    }

    .csv-format-warning {
        padding: 8px 10px; /* Slightly less padding */
        gap: 6px; /* Slightly less gap */
    }

    .csv-format-warning,
    .csv-examples,
    .premises-reference,
    .designations-reference,
    .reference-table,
    .bulk-results,
    .error-details,
    .loading-state,
    .empty-state {
        margin: 10px 0; /* Slightly less margin on small screens */
    }

    .form-group {
        margin-bottom: 14px; /* Slightly less margin */
    }
  }

  @media (max-width: 480px) {
    .info-box {
      padding: 12px;
      gap: 8px;
    }

    .info-box h4 {
      font-size: 12px;
    }

    .info-box p,
    .info-box li {
      font-size: 11px;
      line-height: 1.35;
    }

    .csv-examples code {
        font-size: 10px;
        padding: 1px 2px;
    }

    .table th,
    .table td {
        padding: 8px 10px;
        font-size: 12px;
    }
  }

  /* === Bulk Upload Modal Specific Styles === */
  .upload-method-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .upload-method-selection h4 {
    margin: 0 0 16px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: #ffffff;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .radio-label:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .radio-label input[type="radio"]:checked + span {
    color: #111827;
    font-weight: 600;
  }

  .radio-label span {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
  }

  .bulk-upload-info {
    margin-bottom: 20px;
    width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    display: flex;
    flex-direction: column; /* Stack icon and content vertically */
    align-items: flex-start; /* Align content to the start */
    gap: 12px; /* Space between icon and main content block */
    box-sizing: border-box;
    width: 100%;
    overflow-wrap: break-word;
  }

  .info-box i {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0; /* Don't shrink the icon */
  }

  .info-box > div {
    /* The container for the main text content block */
    width: 100%; /* Take full width */
    overflow-wrap: break-word; /* Allow text inside to break */
  }

  .info-box p {
    margin: 0;
    color: #1e40af;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    /* Ensure paragraphs inside break correctly */
    overflow-wrap: break-word;
  }

  .info-box h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e40af;
  }

  .info-box ul {
    margin: 4px 0;
    padding-left: 16px;
    overflow-wrap: break-word; /* Ensure list items break */
  }

  .info-box li {
    margin: 4px 0;
    font-size: 13px;
    color: #1e40af;
    line-height: 1.4;
    overflow-wrap: break-word; /* Ensure list item text breaks */
  }

  .csv-format-warning {
    background: #fef3c7;
    border: 1px solid #fbbf24;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 10px 0;
    width: 100%;
    box-sizing: border-box;
    overflow-wrap: break-word;
    /* Use flex to align icon and text, allowing text to wrap */
    display: flex;
    align-items: flex-start; /* Align icon to top */
    gap: 8px; /* Space between icon and text */
  }

  .csv-format-warning i {
    color: #d97706;
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0; /* Don't shrink the icon */
  }

  .csv-format-warning strong {
    color: #92400e;
    /* Ensure strong tags inside warning wrap */
    display: inline-block; /* Allows better control over wrapping */
    overflow-wrap: break-word;
  }

  .csv-format-warning code {
    /* Ensure code tags inside warning wrap */
    overflow-wrap: break-word;
    word-break: break-word; /* Break long code snippets */
  }

  /* NEW: Style for ZIP structure example */
  .zip-structure {
    background: #e0f2fe;
    color: #0369a1;
    padding: 8px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.4;
    overflow-x: auto;
    white-space: pre-wrap; /* Wrap long lines */
    word-break: break-all; /* Break if needed */
    margin-top: 6px;
  }

  .csv-examples {
    background: #f0f9ff;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto; /* Scroll horizontally for wide code */
  }

  .csv-examples h5 {
    margin: 0 0 6px 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e40af;
  }

  .csv-examples ul {
    margin: 0;
  }

  .csv-examples li {
    font-size: 12px;
    color: #1e40af;
  }

  .csv-examples code {
    background: #e0f2fe;
    color: #0369a1;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
    white-space: pre-wrap;
    word-break: break-all;
    display: block;
    max-width: 100%;
  }

  .premises-reference,
  .designations-reference {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    width: 100%;
    box-sizing: border-box;
  }

  .premises-reference h4,
  .designations-reference h4 {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  .reference-table {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    margin-top: 10px;
    width: 100%;
    box-sizing: border-box;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
  }

  .table th,
  .table td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    font-size: 13px;
    word-break: break-word;
  }

  .table th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .table td {
    color: #1f2937;
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  .table tr:hover {
    background: #f9fafb;
  }

  .bulk-results {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .bulk-results h4 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #065f46;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .bulk-results h4 i {
    font-size: 18px;
  }

  .error-details {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
  }

  .error-details h5 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
  }

  .error-details ul {
    margin: 0;
    padding-left: 16px;
  }

  .error-details li {
    margin: 3px 0;
    font-size: 12px;
    color: #991b1b;
    line-height: 1.3;
  }

  .loading-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .loading-state .spinner {
    width: 20px;
    height: 20px;
    margin: 0 auto 12px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .empty-state {
    text-align: center;
    padding: 30px 20px;
    background: #ffffff;
  }

  .empty-state i {
    color: #d1d5db;
    font-size: 2rem;
    margin-bottom: 10px;
  }

  .empty-state h4 {
    color: #111827;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 6px 0;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px;
    margin: 0;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* --- NEW RESPONSIVE STYLES --- */
  @media (max-width: 768px) {
    .info-box {
      padding: 14px;
      gap: 10px;
    }

    .info-box h4 {
      font-size: 13px;
    }

    .info-box p,
    .info-box li {
      font-size: 12px;
      line-height: 1.4;
    }

    .csv-format-warning {
        padding: 8px 10px; /* Slightly less padding */
        gap: 6px; /* Slightly less gap */
    }

    .csv-format-warning,
    .csv-examples,
    .premises-reference,
    .designations-reference,
    .reference-table,
    .bulk-results,
    .error-details,
    .loading-state,
    .empty-state {
        margin: 10px 0; /* Slightly less margin on small screens */
    }

    .form-group {
        margin-bottom: 14px; /* Slightly less margin */
    }

    .zip-structure {
        font-size: 11px; /* Slightly smaller font on mobile */
        padding: 6px; /* Slightly less padding */
    }
  }

  @media (max-width: 480px) {
    .info-box {
      padding: 12px;
      gap: 8px;
    }

    .info-box h4 {
      font-size: 12px;
    }

    .info-box p,
    .info-box li {
      font-size: 11px;
      line-height: 1.35;
    }

    .csv-examples code {
        font-size: 10px;
        padding: 1px 2px;
    }

    .table th,
    .table td {
        padding: 8px 10px;
        font-size: 12px;
    }
  }
      `}</style>
    </div>
  );
};

export default StaffManagement;
